/**
 * MONEDA-SERVICE.JS — Fachada SSOT de moneda para todo LotoStream Pro
 *
 * Punto de entrada único que re-exporta presets, formateo y conversión.
 * Un solo import para quien lo consume:
 *
 *   import MonedaService from '@shared/services/moneda/moneda-service.js'
 *   const MonedaService = require('...shared/services/moneda/moneda-service')
 *
 * Compatible: ES Modules (fronts) + CommonJS (backend)
 */

// Re-exports de presets
export {
    PRESETS_MONEDA,
    ORDEN_MONEDAS_PARA_SELECTOR,
    MONEDA_POR_DEFECTO,
    obtenerPresetPorCodigo,
    obtenerTodosLosPresetsEnOrden,
    existeCodigoDeMoneda,
    unidadesMenoresPorUnidad,
    convertirUnidadesAUnidadMenor,
} from './moneda-presets.js';

// Re-exports de formateo
export {
    formatearNumeroConSeparadores,
    formatearCentavosConSimbolo,
    formatearCentavosSoloValor,
    formatearUnidadesConSimbolo,
    obtenerSimbolo,
} from './moneda-formateador.js';

// Re-exports de conversión
export {
    calcularTasaCruzada,
    calcularTasaCruzadaONull,
    convertirCentavosEntreMonedas,
    formatearConversionAproximada,
} from './moneda-conversor.js';

// ============================================================
// IMPORTS PARA NAMESPACE
// ============================================================

import {
    PRESETS_MONEDA,
    ORDEN_MONEDAS_PARA_SELECTOR,
    MONEDA_POR_DEFECTO,
    obtenerPresetPorCodigo,
    obtenerTodosLosPresetsEnOrden,
    existeCodigoDeMoneda,
    unidadesMenoresPorUnidad,
    convertirUnidadesAUnidadMenor,
} from './moneda-presets.js';

import {
    formatearNumeroConSeparadores,
    formatearCentavosConSimbolo,
    formatearCentavosSoloValor,
    formatearUnidadesConSimbolo,
    obtenerSimbolo,
} from './moneda-formateador.js';

import {
    calcularTasaCruzada,
    calcularTasaCruzadaONull,
    convertirCentavosEntreMonedas,
    formatearConversionAproximada,
} from './moneda-conversor.js';

// ============================================================
// FUNCIONES MIGRADAS DE currency-presets.js
// ============================================================

/**
 * Crea un objeto de configuración de moneda desde un preset
 * @param {string} codigoIso - Código ISO
 * @returns {object} { codigo, simbolo, decimales, separadorDecimal, separadorMiles }
 */
export function crearConfigMoneda(codigoIso) {
    const preset = obtenerPresetPorCodigo(codigoIso);
    return {
        codigo: preset.codigo,
        simbolo: preset.simbolo,
        decimales: preset.decimales,
        separadorDecimal: preset.separadorDecimal,
        separadorMiles: preset.separadorMiles,
    };
}

/**
 * Calcula la apuesta máxima basada en premio máximo
 * @param {number} premioMaximoCentavos - Premio máximo en centavos
 * @param {number} multiplicador - Multiplicador de la apuesta (36, 18, 3, etc.)
 * @returns {number} Apuesta máxima en centavos
 */
export function calcularApuestaMaxima(premioMaximoCentavos, multiplicador) {
    return Math.floor(premioMaximoCentavos / multiplicador);
}

// ============================================================
// NAMESPACE (para import default o require)
// ============================================================

const MonedaService = {
    // Presets
    PRESETS_MONEDA,
    ORDEN_MONEDAS_PARA_SELECTOR,
    MONEDA_POR_DEFECTO,
    obtenerPresetPorCodigo,
    obtenerTodosLosPresetsEnOrden,
    existeCodigoDeMoneda,
    unidadesMenoresPorUnidad,
    convertirUnidadesAUnidadMenor,

    // Formateo
    formatearNumeroConSeparadores,
    formatearCentavosConSimbolo,
    formatearCentavosSoloValor,
    formatearUnidadesConSimbolo,
    obtenerSimbolo,

    // Conversión
    calcularTasaCruzada,
    calcularTasaCruzadaONull,
    convertirCentavosEntreMonedas,
    formatearConversionAproximada,

    // Utilidades migradas
    crearConfigMoneda,
    calcularApuestaMaxima,

    // Aliases de compatibilidad (nombres cortos usados históricamente)
    PRESETS: PRESETS_MONEDA,
    ORDER: ORDEN_MONEDAS_PARA_SELECTOR,
    format: formatearCentavosConSimbolo,
    formatValue: formatearCentavosSoloValor,
    formatNumber: formatearNumeroConSeparadores,
    getPreset: obtenerPresetPorCodigo,
    getAllPresets: obtenerTodosLosPresetsEnOrden,
    createConfig: crearConfigMoneda,
    calcularMaxApuesta: calcularApuestaMaxima,
};

export default MonedaService;

// ============================================================
// COMMONJS COMPATIBILITY
// ============================================================

if (typeof module !== 'undefined' && module.exports) {
    module.exports = MonedaService;
}
